'use client';

import { FileCheck2, Lock, MessageCircle } from 'lucide-react';
import { CheckShopStarsRow } from '@/components/checkshop-stars-row';
import { defaultPaymentLogo, getPaymentMethodsForCheckout } from '@/lib/commerce/checkout-payment-methods';

const COPY = {
  nl: { secure: 'Veilig betalen', fileCheck: 'Gratis automatische bestandscheck bij het uploaden', help: 'Vragen? Onze klantenservice helpt je persoonlijk.' },
  en: { secure: 'Secure payment', fileCheck: 'Free automatic file check when you upload', help: 'Questions? Our customer service helps you personally.' },
  de: { secure: 'Sicher bezahlen', fileCheck: 'Kostenlose automatische Dateiprüfung beim Hochladen', help: 'Fragen? Unser Kundenservice hilft Ihnen persönlich.' },
  fr: { secure: 'Paiement sécurisé', fileCheck: 'Contrôle automatique gratuit lors de l’envoi du fichier', help: 'Des questions ? Notre service client vous aide personnellement.' },
} as const;

/**
 * Vertrouwen vlak bij de koopknop: de echte CheckShop-score (live opgehaald),
 * precies de betaalmethoden die de kassa voor dit land toont, en wat we
 * werkelijk doen. Geen verzonnen aantallen, geen afteltimers.
 * Staat onder de knop, zodat de knop zelf nooit verspringt.
 */
export function PurchaseReassurance({ locale, countryCode, variant = 'cart', showPaymentLogos = true }: {
  locale: string;
  countryCode?: string | null;
  variant?: 'cart' | 'product';
  /** False op de kassa: daar staan de betaalmethoden al in de betaalkaart. */
  showPaymentLogos?: boolean;
}) {
  const copy = COPY[String(locale || 'nl').slice(0, 2) as keyof typeof COPY] || COPY.en;
  const methods = getPaymentMethodsForCheckout(countryCode || 'NL', locale)
    .map((method) => ({ method, src: defaultPaymentLogo(method) }))
    .filter((entry): entry is { method: string; src: string } => Boolean(entry.src))
    .slice(0, 6);

  return (
    <div className={`space-y-2.5 ${variant === 'cart' ? 'mt-4 border-t border-gray-100 pt-4' : 'mt-3'}`} data-purchase-reassurance={variant}>
      <div className="flex justify-center">
        <CheckShopStarsRow className="text-xs [&_svg]:h-4 [&_svg]:w-4" />
      </div>
      {/* Op de kassa staat "veilig betalen" met de logo's al bij de knop. */}
      {showPaymentLogos && <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5">
        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-700">
          <Lock className="h-3.5 w-3.5 text-emerald-700" aria-hidden="true" />
          {copy.secure}
        </span>
        {methods.map(({ method, src }) => (
          // Vaste maat: de logo's mogen de regel niet laten verspringen tijdens het laden.
          <img key={method} src={src} alt={method} width={34} height={22} className="h-[22px] w-[34px] rounded border border-gray-200 bg-white object-contain p-0.5" loading="lazy" />
        ))}
      </div>}
      {variant === 'cart' && (
        <ul className="space-y-1.5 text-xs text-gray-600">
          <li className="flex items-start gap-2">
            <FileCheck2 className="mt-px h-3.5 w-3.5 shrink-0 text-emerald-700" aria-hidden="true" />
            <span>{copy.fileCheck}</span>
          </li>
          <li className="flex items-start gap-2">
            <MessageCircle className="mt-px h-3.5 w-3.5 shrink-0 text-emerald-700" aria-hidden="true" />
            <span>{copy.help}</span>
          </li>
        </ul>
      )}
    </div>
  );
}
